import { ensureAuthenticated } from '@lune/auth/ensure'
import { ErrorPage } from '@lune/common/errors'
import { printRouterTree } from '@lune/common/router'
import React from 'react'
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom'
import { version } from './api/version'
import { Dashboard } from './components/dashboard'
import {
  DefaultPage,
  DoneRoute,
  DueTodayRoute,
  LegacyFancyLoginPage,
  LegacyLoginFailed,
  LegacyLoginSuccess,
  LoginFailedPage,
  LoginPage,
  LoginSuccessPage,
  MyTasksRoute,
  OverdueRoute,
  ProjectDetailRoute,
  ProjectsRoute,
  Root,
  TaskDetailRoute,
  TasksRoute
} from './paths'

const PageLogin = React.lazy(
  async () =>
    await import('./pages/login').then((m) => ({ default: m.LoginPage }))
)

// ─── Project pages ─────────────────────────────────────────────────────────

const ProjectsList = React.lazy(
  async () =>
    await import('./pages/projects').then((m) => ({
      default: m.ProjectsListPage
    }))
)

const ProjectDetail = React.lazy(
  async () =>
    await import('./pages/project').then((m) => ({
      default: m.ProjectDetailPage
    }))
)

const TaskDetail = React.lazy(
  async () =>
    await import('./pages/task').then((m) => ({
      default: m.TaskDetailPage
    }))
)

// ─── Task filter pages ─────────────────────────────────────────────────────

const AllTasks = React.lazy(
  async () =>
    await import('./pages/views/AllTasksPage').then((m) => ({
      default: m.AllTasksPage
    }))
)
const MyTasks = React.lazy(
  async () =>
    await import('./pages/views/MyTasksPage').then((m) => ({
      default: m.MyTasksPage
    }))
)
const DueToday = React.lazy(
  async () =>
    await import('./pages/views/DueTodayPage').then((m) => ({
      default: m.DueTodayPage
    }))
)
const Overdue = React.lazy(
  async () =>
    await import('./pages/views/OverduePage').then((m) => ({
      default: m.OverduePage
    }))
)
const Done = React.lazy(
  async () =>
    await import('./pages/views/DonePage').then((m) => ({
      default: m.DonePage
    }))
)

const loginWithoutQueryOrHash = (): string => {
  const url: string = LoginSuccessPage.login.success.url.toString()
  return url.replace(/\?.*$/, '').replace(/#.*$/, '')
}

const routerPaths = [
  {
    path: loginWithoutQueryOrHash(),
    element: <Navigate to={DefaultPage} replace={true} />,
    errorElement: <ErrorPage />
  },
  {
    path: LoginPage.login.url.toString(),
    element: <PageLogin />,
    errorElement: <ErrorPage />
  },
  {
    path: LegacyFancyLoginPage,
    element: <Navigate to={LoginPage.login.url.toString()} replace={true} />,
    errorElement: <ErrorPage />
  },
  {
    path: LegacyLoginSuccess,
    element: (
      <Navigate
        to={LoginSuccessPage.login.success.url.toString()}
        replace={true}
      />
    ),
    errorElement: <ErrorPage />
  },
  {
    path: LegacyLoginFailed,
    element: (
      <Navigate
        to={LoginFailedPage.login.failed.url.toString()}
        replace={true}
      />
    ),
    errorElement: <ErrorPage />
  },
  {
    path: Root.root.url.toString(),
    element: <Dashboard />,
    loader: () => ensureAuthenticated(version, LoginPage, Root),
    errorElement: <ErrorPage />,
    children: [
      {
        path: Root.root.url.toString(),
        element: <Navigate to={DefaultPage} replace={true} />,
        errorElement: <ErrorPage />
      },
      // Projects
      {
        path: ProjectsRoute,
        element: <ProjectsList />,
        errorElement: <ErrorPage />
      },
      {
        path: ProjectDetailRoute,
        element: <ProjectDetail />,
        errorElement: <ErrorPage />
      },
      {
        path: TaskDetailRoute,
        element: <TaskDetail />,
        errorElement: <ErrorPage />
      },
      // Task filter views
      {
        path: TasksRoute,
        element: <AllTasks />,
        errorElement: <ErrorPage />
      },
      {
        path: MyTasksRoute,
        element: <MyTasks />,
        errorElement: <ErrorPage />
      },
      {
        path: DueTodayRoute,
        element: <DueToday />,
        errorElement: <ErrorPage />
      },
      {
        path: OverdueRoute,
        element: <Overdue />,
        errorElement: <ErrorPage />
      },
      {
        path: DoneRoute,
        element: <Done />,
        errorElement: <ErrorPage />
      }
    ]
  }
]

printRouterTree(routerPaths)

const router = createBrowserRouter(routerPaths)

export const App: React.FC = () => <RouterProvider router={router} />
